import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import {
  SEO,
  Link,
  ThemeProvider,
} from '@commercetools-docs/gatsby-theme-docs';
import {
  Card,
  Cards,
} from '@commercetools-docs/gatsby-theme-docs/src/components';
import { designSystem, Markdown, RssFeeds } from '@commercetools-docs/ui-kit';
import { customProperties } from '@commercetools-uikit/design-system';
import CardBannerMcDevsIcon from '../icons/card-banner-mc-devs.svg';
import CardBannerMcDocsIcon from '../icons/card-banner-mc-docs.svg';
import CardBannerPlatformIcon from '../icons/card-banner-platform.svg';
import LogoJavaIcon from '../icons/logo-java.svg';
import LogoJavaScriptIcon from '../icons/logo-javascript.svg';
import LogoPHPIcon from '../icons/logo-php.svg';
import LayoutMarketing, { maxPageWidth } from '../layouts/marketing';

const bannerMinHeight = '200px';
const buttonSurfaceWarning = '#f16d0e';
const buttonSurfaceHover = '#c1570b';
const tabletWidth = '690px';
const heroBackgroundColor = '#005669';

const MainBanner = styled.div`
  min-height: ${bannerMinHeight};
  width: 100%;
  position: relative;

  /* stylelint-disable-next-line function-whitespace-after */
  background-image: ${(props) => `url('${props.heroBackgroundURL}')`};
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-color: ${heroBackgroundColor};
`;
const MainBannerConstraint = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - ${designSystem.dimensions.spacings.m} * 2);
  max-width: ${maxPageWidth};
  margin: 0 auto;
  padding: ${designSystem.dimensions.spacings.m};

  > * + * {
    margin: ${designSystem.dimensions.spacings.m} 0 0;
  }

  @media screen and (min-width: calc(${maxPageWidth} - 0.01px)) {
    width: calc(${maxPageWidth} - ${designSystem.dimensions.spacings.m} * 2);
  }

  @media screen and (min-width: ${tabletWidth}) {
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;

  @media screen and (min-width: ${tabletWidth}) {
    flex-direction: column;
    justify-content: flex-start;
  }
`;
const ButtonLink = styled(Link)`
  display: inline-block;
  padding: ${designSystem.dimensions.spacings.s}
    ${designSystem.dimensions.spacings.m};
  font-size: ${designSystem.typography.fontSizes.body};
  color: ${designSystem.colors.light.textInverted} !important;
  background-color: ${buttonSurfaceWarning};
  border: 1px solid ${buttonSurfaceWarning};
  border-radius: ${customProperties.borderRadius6};

  :hover {
    background-color: ${buttonSurfaceHover};
  }
`;
const TitleAndLogoContainer = styled.div`
  display: flex;
  flex-direction: column;

  > * + * {
    margin: ${designSystem.dimensions.spacings.m} 0 0;

    @media screen and (min-width: ${tabletWidth}) {
      align-self: flex-end;
    }
  }
`;
const PageTitle = styled.h1`
  display: inline-block;
  font-size: ${designSystem.typography.fontSizes.h1};
  font-weight: ${designSystem.typography.fontWeights.regular};
  color: ${designSystem.colors.light.textInverted};
  border-bottom: 1px solid ${designSystem.colors.light.surfacePrimary};
`;
const HeroLogo = styled.div`
  width: 214px;
  height: 44px;

  /* stylelint-disable-next-line function-whitespace-after */
  background-image: ${(props) => `url('${props.heroLogoURL}')`};
  background-repeat: no-repeat;
  background-size: auto;
  background-position: center;
`;
const SectionContainer = styled.div`
  z-index: 1;
  width: calc(100% - ${designSystem.dimensions.spacings.m} * 2);
  max-width: ${maxPageWidth};
  margin: 0 auto;
  padding: 0 ${designSystem.dimensions.spacings.m}
    ${designSystem.dimensions.spacings.big};

  @media screen and (min-width: calc(${maxPageWidth} - 0.01px)) {
    width: calc(${maxPageWidth} - ${designSystem.dimensions.spacings.m} * 2);
  }
`;
const H2 = styled.h2`
  font-size: ${designSystem.typography.fontSizes.h2};
  font-weight: ${designSystem.typography.fontWeights.bold};
  /* padding-bottom: ${designSystem.dimensions.spacings.s}; */
`;
const HomepageSection = styled.section`
  padding-top: ${designSystem.dimensions.spacings.xl};
  padding-bottom: ${designSystem.dimensions.spacings.s};
`;

const PageMarketingContent = (props) => (
  <>
    <MainBanner heroBackgroundURL={props.data.heroBackground.publicURL}>
      <MainBannerConstraint>
        <ButtonContainer>
          <ButtonLink href="/../tutorials/getting-started" noUnderline={true}>
            Get Started
          </ButtonLink>
        </ButtonContainer>
        <TitleAndLogoContainer>
          <div>
            <PageTitle>{'Platform documentation'}</PageTitle>
          </div>
          <HeroLogo heroLogoURL={props.data.heroLogo.publicURL} />
        </TitleAndLogoContainer>
      </MainBannerConstraint>
    </MainBanner>
    <SectionContainer>
      <Markdown.TypographyPage>
        <HomepageSection>
          <H2>Our Product</H2>
          <Cards clickable narrow smallTitle>
            <Card
              title="Developer Tutorials"
              href="/../tutorials/"
              image={<CardBannerPlatformIcon />}
            >
              Guiding you through learning commercetools
            </Card>
            <Card
              title="Platform APIs"
              href="/../api/"
              image={<CardBannerPlatformIcon />}
            >
              Build your dreams using the commercetools APIs
            </Card>
            <Card
              title="Merchant Center"
              href="/../merchant-center/"
              image={<CardBannerMcDocsIcon />}
            >
              The User Interface to manage your business
            </Card>
            <Card
              title="Custom Applications"
              href="https://docs.commercetools.com/custom-applications/"
              image={<CardBannerMcDevsIcon />}
            >
              Build your custom Merchant Center functionality
            </Card>
          </Cards>
        </HomepageSection>
        <HomepageSection>
          <H2>SDKs and Tooling</H2>
          <Cards clickable smallTitle>
            <Card title="Java" href="/../sdk/jvm-sdk" icon={<LogoJavaIcon />}>
              The Java SDKs enable type-safe programming and provide a multitude
              of helpers.
            </Card>
            <Card
              title="JavaScript"
              href="/../sdk/javascript-sdk"
              icon={<LogoJavaScriptIcon />}
            >
              Modular JavaScript libraries for robust implementations includes
              SDK written in TypeScript.
            </Card>
            <Card title="PHP" href="/../sdk/php-sdk" icon={<LogoPHPIcon />}>
              PHP SDK and libraries helps you build apps with PHP native
              interfaces models and helpers.
            </Card>
          </Cards>
          <p>
            <Link href="/../sdk">See all SDKs and tools...</Link>
          </p>
        </HomepageSection>
        <HomepageSection>
          <H2>Release Notes</H2>

          <RssFeeds
            dataSources={[
              'https://docs.commercetools.com/api/releases/feed.xml',
              'https://docs.commercetools.com/merchant-center/releases/feed.xml',
              'https://docs.commercetools.com/custom-applications/releases/feed.xml',
              'https://docs.commercetools.com/import-export/releases/feed.xml',
            ]}
          />
        </HomepageSection>
      </Markdown.TypographyPage>
    </SectionContainer>
  </>
);
PageMarketingContent.displayName = 'PageMarketingContent';
PageMarketingContent.propTypes = {
  data: PropTypes.shape({
    heroBackground: PropTypes.shape({
      publicURL: PropTypes.string.isRequired,
    }).isRequired,
    heroLogo: PropTypes.shape({
      publicURL: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

const PageMarketingTemplate = (props) => (
  <ThemeProvider>
    <LayoutMarketing>
      <SEO
        title="commercetools platform documentation"
        excludeFromSearchIndex={false}
      />
      <PageMarketingContent {...props} />
    </LayoutMarketing>
  </ThemeProvider>
);

export const query = graphql`
  query QueryHomeWebsite {
    heroBackground: file(relativePath: { eq: "hero-background.svg" }) {
      publicURL
    }

    heroLogo: file(relativePath: { eq: "hero-logo.svg" }) {
      publicURL
    }
  }
`;

export default PageMarketingTemplate;
